import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE, apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Btn, EmptyState, Spinner, Money, StatusBadge } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import DocumentEditor from "@/app/components/DocumentEditor";
import { Receipt, Plus, MoreVertical, Download, Send, Banknote, Link2 } from "lucide-react";
import { fmtDate } from "@/lib/format";

export default function Invoices() {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const { data, loading, refetch } = useFetch(`/invoices?status=${status}`, [status]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ amount: "", method: "bank_transfer" });

  useEffect(() => { if (params.get("new")) { setOpen(true); params.delete("new"); setParams(params, { replace: true }); } }, []); // eslint-disable-line

  const create = async (payload) => {
    setSaving(true);
    try { await api.post("/invoices", payload); toast.success("Invoice created"); setOpen(false); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const viewPdf = (id) => window.open(`${API_BASE}/invoices/${id}/pdf`, "_blank");
  const markSent = async (id) => { await api.post(`/invoices/${id}/status`, { status: "sent" }); toast.success("Marked sent"); refetch(); };
  const openPay = (inv) => { setPayFor(inv); setPay({ amount: inv.balance, method: "bank_transfer" }); };
  const copyPayLink = async (inv) => {
    const url = `${window.location.origin}/pay/i/${inv.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Payment link copied — send it to your customer"); }
    catch { window.prompt("Copy this payment link", url); }
  };
  const recordPay = async () => {
    try {
      await api.post(`/invoices/${payFor.id}/payments`, { amount: Number(pay.amount), method: pay.method });
      toast.success("Payment recorded"); setPayFor(null); refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const items = data?.items || [];
  const TABS = ["", "draft", "sent", "partial", "paid", "overdue"];
  return (
    <div className="animate-fade-up">
      <PageHeader title="Invoices & Payments" description="Bill customers and get paid faster."
        action={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="create-invoice-button"><Plus size={16} /> New invoice</Btn>} />
      <div className="flex gap-1 mb-4 overflow-x-auto">
        {TABS.map((t) => <button key={t} onClick={() => setStatus(t)} data-testid={`invoice-tab-${t || "all"}`}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${status === t ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-100"}`}>{t ? t[0].toUpperCase() + t.slice(1) : "All"}</button>)}
      </div>

      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" description="Create an invoice or convert an accepted quote."
          action={<Btn variant="accent" onClick={() => setOpen(true)}><Plus size={16} /> New invoice</Btn>} />
      ) : (
        <div className="amden-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">Number</th><th className="px-4 py-3 font-semibold">Customer</th>
              <th className="px-4 py-3 font-semibold hidden md:table-cell">Due</th>
              <th className="px-4 py-3 font-semibold text-right">Total</th><th className="px-4 py-3 font-semibold text-right hidden sm:table-cell">Balance</th>
              <th className="px-4 py-3 font-semibold">Status</th><th></th></tr></thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50/80" data-testid={`invoice-row-${inv.number}`}>
                  <td className="px-4 py-3 font-mono font-medium text-navy">{inv.number}</td>
                  <td className="px-4 py-3 text-slate-600">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{fmtDate(inv.due_date)}</td>
                  <td className="px-4 py-3 text-right"><Money value={inv.total} symbol={sym} /></td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell"><Money value={inv.balance} symbol={sym} className={inv.balance > 0 ? "text-red-600" : "text-emerald-600"} /></td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button data-testid={`invoice-menu-${inv.number}`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><MoreVertical size={16} /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => viewPdf(inv.id)}><Download size={15} className="mr-2" /> View PDF</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => markSent(inv.id)}><Send size={15} className="mr-2" /> Mark sent</DropdownMenuItem>
                        {inv.balance > 0 && <DropdownMenuItem onClick={() => copyPayLink(inv)} data-testid={`invoice-paylink-${inv.number}`}><Link2 size={15} className="mr-2" /> Copy payment link</DropdownMenuItem>}
                        {inv.balance > 0 && <DropdownMenuItem onClick={() => openPay(inv)}><Banknote size={15} className="mr-2" /> Record payment</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto amden-scroll">
          <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
          <DocumentEditor kind="invoice" onSave={create} saving={saving} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Record payment · {payFor?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-slate-500">Balance due: <Money value={payFor?.balance} symbol={sym} className="text-navy font-semibold" /></p>
            <div><label className="text-sm font-medium text-slate-700">Amount</label>
              <input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono focus:ring-2 focus:ring-blue-500 outline-none" /></div>
            <div><label className="text-sm font-medium text-slate-700">Method</label>
              <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white">
                {["bank_transfer", "card", "cash", "cheque"].map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}</select></div>
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setPayFor(null)}>Cancel</Btn><Btn variant="accent" onClick={recordPay} data-testid="payment-save">Record payment</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
